// src/lib/site.ts — copy and imagery for the public website.
//
// The words are Lotus Living's own (lotusliving.co.uk), reproduced so the demo reads
// exactly like the brand. Photography lives in /public/images and is keyed by the same
// scheme slugs the seed data and the public API use, so a listing card can always find
// its building.

export const SITE_NAV = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Locations', to: '/rent' },
  { label: 'Contact', to: '/contact' },
] as const;

export const FOOTER_MENU = {
  Explore: [
    { label: 'About', to: '/about' },
    { label: 'Locations', to: '/rent' },
    { label: 'Contact', to: '/contact' },
    { label: 'Resident sign in', to: '/login' },
  ],
  Links: [
    { label: 'Privacy Policy', to: '#' },
    { label: 'Terms & Conditions', to: '#' },
    { label: 'Cookie Policy', to: '#' },
    { label: 'Complaints Procedure', to: '#' },
    { label: 'Tenant Fee Schedule', to: '#' },
    { label: 'Book a viewing', to: '/rent' },
  ],
  Follow: [
    { label: 'LinkedIn', to: 'https://uk.linkedin.com/company/lotus-living-london', external: true },
    { label: 'Instagram', to: 'https://www.instagram.com/lotuslivinglondon/', external: true },
    { label: 'Facebook', to: 'https://www.facebook.com/profile.php?id=61572364475620', external: true },
  ],
} as const;

/** Building photography by scheme slug. Falls back to an interior for schemes without one. */
const SCHEME_IMAGES: Record<string, string> = {
  'drake-house': '/images/schemes/drake-house.jpg',
  'elgar-house': '/images/schemes/elgar-house.jpg',
  'the-bays': '/images/schemes/the-bays.jpg',
  'ashleigh-court': '/images/schemes/ashleigh-court.jpg',
  'vivian-court': '/images/schemes/vivian-court.jpg',
  'clarence-court': '/images/schemes/clarence-court.jpg',
  'beechwood-grove': '/images/schemes/beechwood-grove.jpg',
  'raffles-house': '/images/schemes/raffles-house.jpg',
  'madison-court': '/images/schemes/maddison-court.jpg',
  'maddison-court': '/images/schemes/maddison-court.jpg',
  'bellerive-house': '/images/schemes/bellerive-house.jpg',
  'rivington-court': '/images/schemes/rivington-court.jpg',
};

export const INTERIORS = {
  bedroom: '/images/interiors/bedroom.jpg',
  harlow: '/images/interiors/harlow.jpg',
  kitchen: '/images/interiors/kitchen.jpg',
  kitchenDetail: '/images/interiors/kitchen-detail.jpg',
  bathroom: '/images/interiors/bathroom.jpg',
  livingArea: '/images/interiors/living-area.jpg',
  welcomeHamper: '/images/interiors/welcome-hamper.jpg',
  hero: '/images/interiors/hero-couple.jpg',
} as const;

const INTERIOR_CYCLE = [INTERIORS.livingArea, INTERIORS.kitchen, INTERIORS.bedroom, INTERIORS.harlow, INTERIORS.kitchenDetail];

/** The building's own photograph, or null when there is none (no interior stand-in). */
export function buildingPhoto(slug?: string | null): string | null {
  return (slug && SCHEME_IMAGES[slug]) || null;
}

export function schemeImage(slug?: string | null, seed = 0): string {
  if (slug && SCHEME_IMAGES[slug]) return SCHEME_IMAGES[slug]!;
  return INTERIOR_CYCLE[Math.abs(seed) % INTERIOR_CYCLE.length]!;
}

/** Slug from a scheme name when the API only gives us the name (public listings). */
export function slugify(name?: string | null): string {
  return (name ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export const PILLARS = [
  { title: 'By our family, to your family', body: 'Our properties are designed and built to feel just like home. Because we own and manage every building ourselves, we respond to residents quickly, without layers of agents in between.' },
  { title: 'We specialise in build-to-rent', body: 'We have extensive expertise in the build to rent sector and have steadfastly focused on building welcoming, modern and enjoyable homes.' },
  { title: 'A friendly, approachable team', body: 'Our team knows their stuff, offering an outstanding level of customer service and customer care from the first viewing to the day you move on.' },
  { title: 'Exceptional as standard', body: 'We believe that everyone deserves a place they love to call home, delivered through high-quality finishes and thoughtful amenities.' },
] as const;

export const STATS = [
  { value: '1,000', label: 'homes across Greater London' },
  { value: '420', label: 'homes in development' },
  { value: '£300m', label: 'current gross development value' },
  { value: '10,000', label: 'home portfolio, our ambition' },
] as const;

export const TEAM = [
  ['Rajiv Nehru', 'CEO'], ['Joshua Reuben', 'Associate Director'], ['Kevin Belous', 'Residential Director'],
  ['Ryan Nehru', 'Finance Director'], ['Jaya Nehru', 'Interior Design Director'], ['Paul Murphy', 'Project Manager'],
  ['Tanmaya Nigam', 'Financial Manager'], ['Ruchi Agarwal', 'Finance Assistant'], ['Paresh Mistry', 'Estates Manager'],
  ['Emma Ross', 'Lettings Executive'], ['Luke Hayes', 'Property Maintenance'],
] as const;

export const BENEFITS = [
  { title: 'Professional Management', body: 'Our team of dedicated staff handle everything from maintenance and rent collection to personal queries quickly and efficiently.', icon: 'BadgeCheck' },
  { title: 'Legal Compliance', body: "When it comes to laws and regulations, you don't want to hope you're legally protected, you want to know you are.", icon: 'Scale' },
  { title: 'Consistent Policies', body: 'Standardised legal agreements provide clarity and consistency all round.', icon: 'FileCheck2' },
  { title: 'Maintenance', body: 'Your home is your haven so any maintenance needs will be dealt with as swiftly as possible.', icon: 'Wrench' },
  { title: 'Emergency Support', body: "From plumbing to electrics, should any emergencies arise, we've got your back 24/7.", icon: 'PhoneCall' },
  { title: 'Peace of Mind', body: 'Enjoy the assurance that comes with a professionally managed property.', icon: 'ShieldCheck' },
  { title: 'Family-Centred Service', body: 'We are a family-run business, treating every tenant with the personal respect and care that we would our own family.', icon: 'HeartHandshake' },
] as const;

export const TESTIMONIALS = [
  { name: 'Rosa', quote: 'Very helpful from day one on any queries or issues I had during the flat set-up and during the lease period.' },
  { name: 'Simona', quote: 'The management consistently ensures the cleanliness of common areas and maintains a safe and tranquil environment.' },
  { name: 'James', quote: 'They maintain the property to a high standard and are always thinking of ways to improve. Communication is fantastic.' },
  { name: 'Maya', quote: 'Paresh has always been professional and helpful and if I have ever had any enquiries, I get a response straight away.' },
] as const;

export const FAQS = [
  ['What level of deposit is required?', "A deposit equivalent to 5 weeks' rent is required for all tenancies, in accordance with UK regulations. It is protected with a government-approved scheme within 30 days."],
  ['Are there accessible rooms?', 'Yes! Some of our properties include accessible apartments.'],
  ['What is the tenancy length?', 'All tenancies are offered on a rolling periodic basis, providing flexibility and long-term security for residents.'],
  ['Do I need a guarantor?', "Depending on your personal circumstances a guarantor may be required. This isn't always the case, however."],
  ['Are all apartments furnished?', 'We offer furnished and unfurnished apartments so you can decide which option best fits your needs.'],
  ['What documents do I require?', 'Valid ID, proof of your right to rent in the UK (a UK visa if applicable), proof of income, proof of current address, and a landlord reference.'],
  ['Do you offer short-term lets?', 'We currently offer long-term tenancies only and do not provide short-term lets.'],
  ['Is council tax included?', "Council tax is not included in the rent and is the resident's responsibility unless otherwise stated."],
  ['Will I need contents insurance?', "It's highly advisable to have contents insurance to protect your belongings."],
  ['Can I bring my pet?', 'Pets may be permitted subject to approval and the terms of your tenancy agreement.'],
  ['Can I share my room with a partner?', 'Room sharing may be permitted depending on the apartment type and occupancy guidelines.'],
  ['Are you selling the homes?', 'Our build-to-rent properties are not for sale.'],
  ['How many keys are issued per apartment?', 'The number of keys issued will depend on the apartment type and the number of approved occupants.'],
  ['Is there bicycle storage?', 'Yes! All properties offer bicycle storage facilities.'],
  ['Is there a telephone line provided in the apartment?', 'Yes. Every property has a telephone connection point.'],
  ['Are appliances included?', 'Each property has a good quality fridge-freezer, oven, electric hob, washer dryer, and a dishwasher.'],
] as const;
